/**
 * Subject Profile adapter (PROD-027, OSINT-MISSION-29).
 *
 * The Subject Profile product is a collection of SEC-051 (Subject
 * Identification) cards — one per subject the mission profiled. Its single
 * section (SEC-051) carries a per-subject list under either `elements[]`
 * (plural key) or `element` (singular key, value still an array) — see
 * `resolveSubjectElements`'s doc comment for why both are real. Each entry
 * is the SEC-051 body for one subject (the exact prop shape gw-ui's
 * SubjectIdentificationSection consumes).
 *
 * This adapter maps a section's resolved subject-element list → one
 * SubjectIdentificationSectionProps per subject. The old primary-only
 * adapter lived in gw-mission-runner's viewer (being torn down); this is its
 * multi-subject successor in gw-spa.
 *
 * Falls back to the single-subject `section.bodyProps` shape when neither
 * `elements[]` nor `element` carries any entries. Never fabricates fields
 * the element does not carry — every SubjectIdentificationSection prop
 * defaults to an honest-empty value (empty string / empty array / empty
 * object).
 *
 * GW-9864: `mapRunProductToSubjectProfileAssembly` maps the REAL
 * `GET /v1/missions/{missionId}/runs/{runId}/products/{productId}` response
 * (`RunProduct`, mirroring `handler/products.go`'s `Product`) to the
 * `RunReportAssembly` shape the run-results page family already renders
 * from — the live-path counterpart to `missionRunResultsSlice.ts`'s
 * `emptySubjectProfile()` fallback.
 */

import { auditEventDetail } from '@gw/ui/types';

import type {
  PredicateGap,
  PredicateProvenance,
  RunProduct,
  RunProductSection,
} from '@/api/missionsDirectApiClient';
import type { RunReportAssembly, RunReportSection } from '@/types/runReport.types';
import type {
  AdmiraltyCredibility,
  AdmiraltyReliability,
  AuditEvent,
  AuditTrailFootnote,
  SectionEnvelope,
} from '@gw/ui/types';
import type { ExecutiveSummarySectionProps, SubjectIdentificationSectionProps } from '@gw/ui';

/** Raw per-subject SEC-051 body as delivered by the product assembler. */
interface SubjectElement {
  entityCanonicalId?: string;
  fullName?: string;
  entityType?: string;
  entitySubtype?: string;
  aliases?: string[];
  knownIdentifiers?: Record<string, string | number>;
  predicateAttributes?: Record<string, unknown>;
  requiredPredicates?: string[];
  recommendedPredicates?: string[];
  /**
   * GW-12329: the real wire shape is `PredicateGap[]` (objects), though the
   * older writer path still emits a bare `string[]`. Normalized to display
   * strings for gw-ui by `normalizeMissingPredicates` — passing a raw
   * `PredicateGap` object straight through is exactly what rendered as
   * "[object Object]" (GW-12262).
   */
  missingPredicates?: Array<string | PredicateGap>;
  disambiguationStatement?: string;
  /**
   * GW-12491 §3.1: element-level per-predicate provenance
   * (`dict[predicateId -> PredicateProvenance[]]`, the client wire type). Each
   * record carries the wire-only `auditTrailIds` (PROD-CORE-003 event ids that
   * back that predicate's value; a subset of the section envelope's
   * `auditTrailIds[]`). The claim→citation set for a predicate is the union of
   * `provenance[predicateId][*].auditTrailIds`, mapped to a footnote index by
   * `buildPredicateCitations`. Absent ⇒ no `predicateCitations` prop ⇒ no
   * marker (no-op-safe; pre-emission runs render exactly as before).
   */
  provenance?: Record<string, PredicateProvenance[]>;
}

/**
 * Resolves a SEC-051 section's per-subject list from either real wire
 * shape. Two distinct, currently-live gw-mission-engine producer paths write
 * the same productId/sectionId with different key casing (GW-10693):
 *   - `elements` (plural): the older `assemble_product.py` writer.
 *   - `element` (singular key, array value — only the KEY is singular, the
 *     value is still an array, one entry per subject): the current primary
 *     production writer (`ProductionAgent` -> `create_section.py`/
 *     `create_product.py`), the live dispatch target for OSINT-MISSION-29
 *     today.
 * Both are first-class alternate keys, not one canonical + one fallback —
 * `elements` is checked first (existing behavior), `element` second.
 */
function resolveSubjectElements(
  section: { elements?: SubjectElement[]; element?: SubjectElement[] } | undefined
): SubjectElement[] {
  if (!section) return [];
  if (Array.isArray(section.elements) && section.elements.length > 0) {
    return section.elements;
  }
  if (Array.isArray(section.element) && section.element.length > 0) {
    return section.element;
  }
  return [];
}

/**
 * Raw producer-record shape a `predicateAttributes` value can arrive in
 * instead of a clean scalar/plain-object (GW-11111/GW-11114 finding): some
 * gw-mission-engine predicate writers (observed for HAS_ADDRESS,
 * HAS_DATE_OF_BIRTH, HAS_EMAIL_ADDRESS, HAS_PHONE_NUMBER) emit the whole
 * provider-record envelope — `{datasetId, predicates, records: [{data,
 * dataset_version, likelihood, status}], subjectCanonicalId}` — rather than
 * the resolved value. This is being fixed at the source in gw-mission-engine
 * (GW-11112) and defensively re-rendered in gw-ui (GW-11113); this adapter
 * normalizes it too so gw-spa never forwards the raw envelope to
 * `SubjectIdentificationSection` regardless of which upstream fix has
 * landed for a given predicate/run.
 */
interface RawPredicateRecordEnvelope {
  records: Array<{ data?: unknown }>;
}

function isRawPredicateRecordEnvelope(value: unknown): value is RawPredicateRecordEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Array.isArray((value as { records?: unknown }).records)
  );
}

/** Extracts one record's `data` payload, unwrapping a single-key object to its scalar value. */
function extractRecordValue(record: { data?: unknown }): unknown {
  const { data } = record;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const values = Object.values(data as Record<string, unknown>);
    return values.length === 1 ? values[0] : data;
  }
  return data;
}

/**
 * Normalizes a single `predicateAttributes` value: unwraps a raw producer
 * records-envelope to the scalar/plain-object value `SubjectIdentificationSection`
 * expects, and passes through any value that is already in that clean shape.
 */
function normalizePredicateValue(value: unknown): unknown {
  if (!isRawPredicateRecordEnvelope(value)) return value;
  const { records } = value;
  if (records.length === 0) return undefined;
  if (records.length === 1) return extractRecordValue(records[0]);
  return records.map(extractRecordValue);
}

/** Normalizes every value in a `predicateAttributes` map, dropping keys that resolve to nothing. */
function normalizePredicateAttributes(
  attributes: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!attributes) return {};
  const normalized: Record<string, unknown> = {};
  for (const [key, rawValue] of Object.entries(attributes)) {
    const value = normalizePredicateValue(rawValue);
    if (value !== undefined) {
      normalized[key] = value;
    }
  }
  return normalized;
}

/**
 * Normalizes `missingPredicates` to the `string[]` gw-ui's
 * `SubjectIdentificationSection` consumes (GW-12329/GW-12262). The real wire
 * shape is `PredicateGap[]` (objects — `{predicate, reasonCode, detail}`);
 * some older writers still emit a bare `string[]`. Each object collapses to
 * its `predicate` id — passing the object straight through is what rendered as
 * "[object Object]". Entries with no resolvable predicate id are dropped.
 */
function normalizeMissingPredicates(raw: Array<string | PredicateGap> | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry && typeof entry === 'object' && typeof entry.predicate === 'string') {
        return entry.predicate;
      }
      return '';
    })
    .filter((predicate) => predicate.length > 0);
}

/**
 * Maps one raw SEC-051 subject element to the gw-ui component's prop shape.
 * When `indexByEventId` carries the section's footnote ordinals (GW-12500), the
 * element's per-predicate `provenance` is mapped onto the `predicateCitations`
 * prop; absent/empty ⇒ the prop is omitted and no inline marker renders.
 */
function toSectionProps(
  element: SubjectElement,
  indexByEventId?: Map<string, number>
): SubjectIdentificationSectionProps {
  const props: SubjectIdentificationSectionProps = {
    entityCanonicalId: element.entityCanonicalId ?? '',
    fullName: element.fullName ?? '',
    entityType: element.entityType ?? 'unknown',
    entitySubtype: element.entitySubtype ?? '',
    aliases: element.aliases ?? [],
    knownIdentifiers: element.knownIdentifiers ?? {},
    predicateAttributes: normalizePredicateAttributes(element.predicateAttributes),
    requiredPredicates: element.requiredPredicates ?? [],
    recommendedPredicates: element.recommendedPredicates ?? [],
    missingPredicates: normalizeMissingPredicates(element.missingPredicates),
    disambiguationStatement: element.disambiguationStatement ?? '',
  };
  if (indexByEventId && indexByEventId.size > 0) {
    const predicateCitations = buildPredicateCitations(element.provenance, indexByEventId);
    if (Object.keys(predicateCitations).length > 0) {
      props.predicateCitations = predicateCitations;
    }
  }
  return props;
}

/**
 * Extract every subject card from the Subject Profile's SEC-051 section.
 * Prefers the multi-subject list, checking `elements[]` (plural) then
 * `element` (singular key, array value) — see `resolveSubjectElements`'s
 * doc comment for why both are real, first-class shapes. Falls back to the
 * section's single `bodyProps` subject when neither carries any entries.
 * Returns an empty array when the section itself is missing (honest-empty
 * product).
 */
export function toSubjectIdentificationSections(
  section: RunReportSection | undefined,
  footnotes?: AuditTrailFootnote[]
): SubjectIdentificationSectionProps[] {
  if (!section) return [];

  // GW-12500: when the section's rendered footnotes are supplied, each
  // subject's `provenance` is mapped onto the `predicateCitations` prop via the
  // shared eventId→footnote-index lookup. Omitting `footnotes` (the current
  // call sites, and any pre-emission run) yields no citations — identical to
  // the pre-GW-12500 behavior.
  const indexByEventId = footnoteIndexByEventId(footnotes);

  const elements = resolveSubjectElements(
    section as { elements?: SubjectElement[]; element?: SubjectElement[] }
  );
  if (elements.length > 0) {
    return elements.map((element) => toSectionProps(element, indexByEventId));
  }

  // Single-subject fallback: the PROD-006 fixture SEC-051 carries the subject
  // directly on bodyProps (no elements[]/element wrapper).
  const single = section.bodyProps as SubjectElement | undefined;
  if (single && (single.fullName || single.entityCanonicalId)) {
    return [toSectionProps(single, indexByEventId)];
  }
  return [];
}

/**
 * Collects subject image URLs from a SEC-051 section's resolved elements.
 * Reads `HAS_IMAGE_URL` (string or string[]) and `profile_image_url` (string)
 * from each element's `predicateAttributes`, deduplicating across subjects.
 * Used to populate the `backgroundImages` prop of the mission product hero card.
 */
export function extractSubjectImageUrls(section: RunReportSection | undefined): string[] {
  const elements = resolveSubjectElements(
    section as { elements?: SubjectElement[]; element?: SubjectElement[] }
  );
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const element of elements) {
    const attrs = element.predicateAttributes;
    if (!attrs) continue;
    const hasImageUrl = attrs['HAS_IMAGE_URL'];
    if (typeof hasImageUrl === 'string' && hasImageUrl) {
      if (!seen.has(hasImageUrl)) {
        seen.add(hasImageUrl);
        urls.push(hasImageUrl);
      }
    } else if (Array.isArray(hasImageUrl)) {
      for (const url of hasImageUrl) {
        if (typeof url === 'string' && url && !seen.has(url)) {
          seen.add(url);
          urls.push(url);
        }
      }
    }
    const profileImageUrl = attrs['profile_image_url'];
    if (typeof profileImageUrl === 'string' && profileImageUrl && !seen.has(profileImageUrl)) {
      seen.add(profileImageUrl);
      urls.push(profileImageUrl);
    }
  }
  return urls;
}

const ADMIRALTY_RELIABILITY_GRADES: ReadonlySet<AdmiraltyReliability> = new Set([
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
]);

const VALID_OVERALL_RISK_RATINGS: ReadonlySet<'green' | 'amber' | 'red'> = new Set([
  'green',
  'amber',
  'red',
]);

/**
 * Resolves a section's real provenance envelope from the live PROD-027 wire
 * section (GW-12329). gw-mission-engine now persists the per-section envelope
 * (GW-12319) and gw-missions-api documents it (GW-12323): `admiraltyReliability`
 * (A–F), `admiraltyCredibility` (1–6), `sourcesCited`, `dataGaps`. This maps
 * those camelCase wire fields onto gw-ui's snake_case `SectionEnvelope`.
 *
 * The degraded `F`/`6` ("cannot be judged") code — which gw-ui's own
 * `AdmiraltyGrade` treats as the canonical unknown/degraded case (renders `—`,
 * no tooltip), and empty `sources_cited`/`data_gaps` — is used ONLY as an
 * honest per-field fallback when the engine did not report that field for this
 * run. It is never fabricated over a real value: the old `liveSectionEnvelope`
 * hard-coded `F`/`6` unconditionally, which is exactly the bug this replaces.
 */
function resolveSectionEnvelope(
  sectionId: string,
  apiSection:
    | Pick<
        RunProductSection,
        'admiraltyReliability' | 'admiraltyCredibility' | 'sourcesCited' | 'dataGaps'
      >
    | undefined
): SectionEnvelope {
  const reliability = apiSection?.admiraltyReliability;
  const credibility = apiSection?.admiraltyCredibility;
  return {
    section_id: sectionId,
    admiralty_reliability:
      typeof reliability === 'string' && ADMIRALTY_RELIABILITY_GRADES.has(reliability)
        ? reliability
        : 'F',
    admiralty_credibility:
      typeof credibility === 'number' &&
      Number.isInteger(credibility) &&
      credibility >= 1 &&
      credibility <= 6
        ? (credibility as AdmiraltyCredibility)
        : 6,
    sources_cited: Array.isArray(apiSection?.sourcesCited) ? apiSection.sourcesCited : [],
    data_gaps: Array.isArray(apiSection?.dataGaps) ? apiSection.dataGaps : [],
  };
}

/**
 * The seven AuditEvent types that resolve to a section-provenance footnote —
 * gw-ui's `AuditTrailFootnote.eventType` union. Includes `quality_gate`
 * (PRP-PLAT-056 §5.2 / GW-12135): quality gate outcomes are provenance-bearing
 * events that must appear in section footnotes with their `stepId` detail.
 * `inference` is also retained (PO decision — attributed inference footnotes
 * must render for section-level provenance).
 */
const FOOTNOTE_EVENT_TYPES: ReadonlySet<AuditTrailFootnote['eventType']> = new Set([
  'taxonomy_load',
  'control_message',
  'query_marketplace',
  'apify_search',
  'apify_onboard',
  'inference',
  'quality_gate',
]);

/**
 * Builds the section-level provenance footnotes gw-ui's Subject Profile
 * "Audit Trail" surface renders (GW-12329). Joins a section's real
 * `auditTrailIds` (from its envelope) against the run's PROD-CORE-003 Audit
 * Trail assembly events — this repurposes that surface to show section
 * provenance, NOT the separate PROD-CORE-003 Audit Trail product. Footnotes
 * are numbered in the order the section lists its ids; an id with no matching
 * event (or a `quality_gate` event) is skipped rather than fabricated. Returns
 * an empty array when the section carries no ids or no events are available —
 * gw-ui renders its "No audit trail events yet" placeholder for that.
 */
export function buildSectionFootnotes(
  auditTrailIds: string[] | undefined,
  events: AuditEvent[] | undefined
): AuditTrailFootnote[] {
  if (!Array.isArray(auditTrailIds) || auditTrailIds.length === 0) return [];
  if (!Array.isArray(events) || events.length === 0) return [];

  const eventById = new Map(events.map((event) => [event.id, event]));
  const footnotes: AuditTrailFootnote[] = [];
  let index = 1;
  for (const id of auditTrailIds) {
    const event = eventById.get(id);
    if (!event) continue;
    if (!FOOTNOTE_EVENT_TYPES.has(event.eventType as AuditTrailFootnote['eventType'])) continue;
    footnotes.push({
      index: index++,
      eventId: event.id,
      eventType: event.eventType as AuditTrailFootnote['eventType'],
      label: auditEventDetail(event),
      resultClass: event.resultClass,
      timestamp: event.timestamp,
      latencyMs: event.latencyMs,
    });
  }
  return footnotes;
}

/**
 * GW-12491 (GW-12500): maps a rendered section footnote list to an
 * `eventId → 1-based footnote index` lookup. This is the ONLY bridge from wire
 * `auditTrailId`s (which live in the audit-event id namespace) to the footnote
 * ordinals gw-ui's `SectionFootnoteMarker` resolves against — `buildSectionFootnotes`
 * already assigns each footnote its `index` and stamps the backing `event.id`
 * as `eventId`. The first footnote wins on the (not expected) duplicate-id case.
 */
function footnoteIndexByEventId(footnotes: AuditTrailFootnote[] | undefined): Map<string, number> {
  const indexByEventId = new Map<string, number>();
  if (!Array.isArray(footnotes)) return indexByEventId;
  for (const footnote of footnotes) {
    if (typeof footnote.eventId === 'string' && !indexByEventId.has(footnote.eventId)) {
      indexByEventId.set(footnote.eventId, footnote.index);
    }
  }
  return indexByEventId;
}

/**
 * Resolves a claim's `auditTrailIds` union to the SINGLE 1-based footnote index
 * gw-ui's citation props carry (`predicateCitations[key]`, `topFindingCitations[i]`) —
 * the earliest (lowest-index) footnote among the claim's citing events. Ids with
 * no rendered footnote (skipped by `buildSectionFootnotes` — no matching or
 * non-footnote-type event) are ignored; returns `undefined` when none resolve, so
 * the caller omits the citation and no marker renders (no-op-safe).
 */
function resolveCitationIndex(
  auditTrailIds: string[] | undefined,
  indexByEventId: Map<string, number>
): number | undefined {
  if (!Array.isArray(auditTrailIds) || auditTrailIds.length === 0) return undefined;
  let earliest: number | undefined;
  for (const id of auditTrailIds) {
    const footnoteIndex = indexByEventId.get(id);
    if (footnoteIndex != null && (earliest === undefined || footnoteIndex < earliest)) {
      earliest = footnoteIndex;
    }
  }
  return earliest;
}

/**
 * SEC-051 (GW-12491 §3.1): builds gw-ui's `predicateCitations` map
 * (`predicateId → 1-based footnote index`) from the element's per-predicate
 * `provenance`. Each predicate's citing events = the union of its records'
 * `auditTrailIds`; that union resolves to one footnote index via
 * `resolveCitationIndex`. Predicates whose union resolves to no rendered
 * footnote are omitted, so the returned map only contains cited predicates
 * (empty ⇒ caller omits the prop entirely).
 */
function buildPredicateCitations(
  provenance: Record<string, PredicateProvenance[]> | undefined,
  indexByEventId: Map<string, number>
): Record<string, number> {
  const citations: Record<string, number> = {};
  if (!provenance || indexByEventId.size === 0) return citations;
  for (const [predicateId, records] of Object.entries(provenance)) {
    if (!Array.isArray(records)) continue;
    const auditTrailIds: string[] = [];
    for (const record of records) {
      if (record && Array.isArray(record.auditTrailIds)) {
        auditTrailIds.push(...record.auditTrailIds);
      }
    }
    const footnoteIndex = resolveCitationIndex(auditTrailIds, indexByEventId);
    if (footnoteIndex != null) {
      citations[predicateId] = footnoteIndex;
    }
  }
  return citations;
}

/**
 * SEC-001 (GW-12491 §3.2): builds gw-ui's `topFindingCitations` array,
 * positionally parallel to `topFindings` — entry `i` is the 1-based footnote
 * index citing `topFindings[i]`, from the index-aligned wire
 * `topFindingsCitations[i]` (that finding's `auditTrailId[]`). A finding with no
 * citations, or whose ids resolve to no rendered footnote, gets `null` (no
 * marker). Returns `undefined` when no finding resolves any citation, so the
 * caller omits the prop entirely (no-op-safe).
 */
function buildTopFindingCitations(
  topFindings: string[],
  topFindingsCitations: string[][] | undefined,
  indexByEventId: Map<string, number>
): Array<number | null> | undefined {
  if (!Array.isArray(topFindingsCitations) || indexByEventId.size === 0) return undefined;
  const citations = topFindings.map((_, i) => {
    const footnoteIndex = resolveCitationIndex(topFindingsCitations[i], indexByEventId);
    return footnoteIndex ?? null;
  });
  return citations.some((entry) => entry != null) ? citations : undefined;
}

/**
 * Raw SEC-001 (Executive Summary) synthesis body as delivered by the current
 * gw-mission-engine writer (GW-12330). Unlike SEC-051's per-subject `element`
 * list, PROD-027's SEC-001 carries `element` as a SINGLE object (a
 * `synthesis` section — `build_prod_027`/`create_section.py`), with camelCase
 * fields on the wire (`subjectNames`, `overallRiskRating`, `topFindings`),
 * confirmed against a real dev S3 artifact
 * (`gw-mission-engine/docs/findings/GW-11847-prod027-sec001-root-cause.md`).
 */
export interface ExecutiveSummaryElement {
  subjectNames?: string[];
  subjectTypes?: Record<string, string>;
  overallRiskRating?: 'green' | 'amber' | 'red';
  topFindings?: string[];
  /**
   * GW-12491 §3.2: index-aligned with `topFindings` — element `i` is the
   * `auditTrailId[]` supporting `topFindings[i]`. Mapped onto gw-ui's
   * `topFindingCitations` prop by `buildTopFindingCitations`. Absent, or an
   * empty inner array for a finding, ⇒ no marker on that finding (no-op-safe).
   * `overallRiskRatingCitation` has no wire source in the locked contract, so
   * it is intentionally never emitted.
   */
  topFindingsCitations?: string[][];
}

/**
 * Resolves a SEC-001 section's synthesis body. The `element` key is shared
 * with SEC-051 but here holds a single object, not a per-subject array — so a
 * value that is a plain object (not an array) is the executive-summary body;
 * anything else (missing, or an array) yields `undefined` (no SEC-001).
 */
function resolveExecutiveSummaryElement(
  section: RunProductSection | undefined
): ExecutiveSummaryElement | undefined {
  if (!section) return undefined;
  const raw: unknown = section.element;
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as ExecutiveSummaryElement;
  }
  return undefined;
}

/**
 * Maps the raw SEC-001 synthesis body to gw-ui's `ExecutiveSummarySection` prop
 * shape. When `footnotes` are supplied (GW-12500), the wire
 * `topFindingsCitations` are mapped onto the index-aligned `topFindingCitations`
 * prop; absent/empty ⇒ the prop is omitted and no per-finding marker renders.
 */
export function toExecutiveSummaryProps(
  element: ExecutiveSummaryElement,
  footnotes?: AuditTrailFootnote[]
): ExecutiveSummarySectionProps {
  const props: ExecutiveSummarySectionProps = {
    subjectNames: Array.isArray(element.subjectNames)
      ? element.subjectNames
      : element.subjectNames
        ? [element.subjectNames]
        : [],
    overallRiskRating: (() => {
      if (typeof element.overallRiskRating !== 'string') return 'green';
      const lower = element.overallRiskRating.toLowerCase() as 'green' | 'amber' | 'red';
      if (!VALID_OVERALL_RISK_RATINGS.has(lower)) {
        // eslint-disable-next-line no-console
        console.warn(
          `[subjectProfileAdapter] Unknown overallRiskRating "${element.overallRiskRating}" — clamping to "green" (GW-13204)`
        );
        return 'green';
      }
      return lower;
    })(),
    topFindings: Array.isArray(element.topFindings)
      ? element.topFindings
      : element.topFindings
        ? [element.topFindings]
        : [],
    ...(element.subjectTypes
      ? { subjectTypes: element.subjectTypes as ExecutiveSummarySectionProps['subjectTypes'] }
      : {}),
  };
  const indexByEventId = footnoteIndexByEventId(footnotes);
  if (indexByEventId.size > 0) {
    const topFindingCitations = buildTopFindingCitations(
      props.topFindings,
      element.topFindingsCitations,
      indexByEventId
    );
    if (topFindingCitations) {
      props.topFindingCitations = topFindingCitations;
    }
  }
  return props;
}

/**
 * Maps the real `GET /v1/missions/{missionId}/runs/{runId}/products/{productId}`
 * response to the `RunReportAssembly` shape `RunResultsPage`/
 * `RunProductReportPage`-family pages already render from. Confirmed against
 * `handler/products.go`'s `Product`/`ProductSection`/`SubjectProfileElement`
 * Go structs and this repo's `RunProduct` client types (GW-9864). PROD-027
 * carries `SEC-001-executive-summary` (a synthesis section, `element` = single
 * object) followed by `SEC-051-subject-identification` (per-subject `element`
 * list); both are normalized here to the short `SEC-001`/`SEC-051` ids the
 * page family keys on. Resolves SEC-051 subjects from either real wire key
 * (`elements[]` or singular `element` — see `resolveSubjectElements`, GW-10693).
 *
 * GW-12330: each section's `bodyProps` is populated with the resolved,
 * on-screen data (SEC-001's executive-summary props; SEC-051's mapped subject
 * cards under `subjects`) so the report-wide Copy JSON and PDF — which
 * serialize from `bodyProps` (`buildReportJsonPayload`/`sectionToPdfPage`) —
 * carry real content instead of an empty `{}`.
 */
export function mapRunProductToSubjectProfileAssembly(product: RunProduct): RunReportAssembly {
  const subjectApiSection = product.sections.find(
    (s) => s.sectionId === 'SEC-051' || s.sectionId === 'SEC-051-subject-identification'
  );
  const executiveApiSection = product.sections.find(
    (s) => s.sectionId === 'SEC-001' || s.sectionId === 'SEC-001-executive-summary'
  );
  const elements = resolveSubjectElements(subjectApiSection);
  const executiveSummary = resolveExecutiveSummaryElement(executiveApiSection);

  const sections: RunReportSection[] = [];

  // SEC-001 (Executive Summary) — always first per PROD-027.yaml when present.
  if (executiveSummary) {
    const executiveSection = {
      sectionId: 'SEC-001',
      name: 'Executive Summary',
      envelope: resolveSectionEnvelope('SEC-001', executiveApiSection),
      auditTrailIds: executiveApiSection?.auditTrailIds ?? [],
      bodyProps: toExecutiveSummaryProps(executiveSummary) as unknown as Record<string, unknown>,
      // GW-12702: retain the raw synthesis element (with `topFindingsCitations`)
      // alongside the resolved bodyProps — mirrors SEC-051's raw `elements`
      // stash. The page re-maps this through `toExecutiveSummaryProps` WITH the
      // section's footnotes to attach `topFindingCitations`, which the
      // citation-free `bodyProps` (kept for the report-wide Copy JSON / PDF)
      // cannot carry.
      element: executiveSummary,
    } as unknown as RunReportSection;
    sections.push(executiveSection);
  }

  // SEC-051 (Subject Identification) — one per-subject card list.
  if (subjectApiSection) {
    const subjectSection = {
      sectionId: 'SEC-051',
      name: 'Subject Identification',
      envelope: resolveSectionEnvelope('SEC-051', subjectApiSection),
      auditTrailIds: subjectApiSection.auditTrailIds ?? [],
      bodyProps: {},
      elements,
    } as unknown as RunReportSection;
    // Cache the resolved, on-screen subject cards in bodyProps so Copy JSON /
    // PDF (which read bodyProps) carry the same data the page renders — the raw
    // `elements` above stay the input `toSubjectIdentificationSections` reads.
    subjectSection.bodyProps = { subjects: toSubjectIdentificationSections(subjectSection) };
    sections.push(subjectSection);
  }

  return {
    productId: 'PROD-027',
    productName: 'Subject Profile',
    headline:
      elements.length > 0
        ? `${elements.length} subject${elements.length === 1 ? '' : 's'} profiled: ${elements
            .map((e) => e.fullName)
            .filter(Boolean)
            .join(', ')}.`
        : '',
    // GW-12460: a distinct section-completeness subtitle (gw-ui MissionProductPage's
    // documented `assemblySummary` contract) rather than echoing the subject-count
    // phrase already stated in `headline`. Empty when nothing was assembled — the
    // headline is empty there too, so there is nothing to summarize.
    assemblySummary:
      sections.length > 0
        ? `${sections.length} section${sections.length === 1 ? '' : 's'} assembled`
        : '',
    sections,
  };
}
